'use client';

import { usePathname } from 'next/navigation';
import { Menu, X, Sun, Moon } from 'lucide-react';
import GrvLogo from '@/components/GrvLogo';
import { AppLink } from '@/lib/appLink';
import { useCmsData, useSiteSettings } from '@/lib/CmsContext';
import { getHeaderCta, getNavMenuItems, isNavItemActive } from '@/lib/navMenus';

const defaultNavLinks = [
  { label: 'Будівництво', href: '/budivnytstvo' },
  { label: 'Фасад', href: '/fasad' },
  { label: 'Ремонт', href: '/remont' },
  { label: 'Про Нас', href: '/about-us' },
  { label: 'Наші роботи', href: '/our-works' },
  { label: 'Контакти', href: '/contacts' },
];

export default function Header() {
  const cms = useCmsData();
  const { logo, logoUrl } = useSiteSettings();
  const navLinks = getNavMenuItems(cms, 'primary', defaultNavLinks);
  const headerCta = getHeaderCta(cms);
  const pathname = usePathname() || '/';

  return (
    <header data-grv-header className="grv-header fixed top-0 left-0 right-0 z-50 transition-all duration-500">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
        <AppLink href="/" className="text-3xl" data-grv-logo>
          <GrvLogo logoUrl={logoUrl} alt={logo} />
        </AppLink>

        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((link) => {
            const isActive = isNavItemActive(pathname, link.href);
            return (
              <AppLink
                key={`${link.label}-${link.href}`}
                href={link.href}
                data-grv-nav
                data-active={isActive ? 'true' : undefined}
                className="grv-header-nav text-sm font-medium transition-colors duration-300"
              >
                {link.label}
              </AppLink>
            );
          })}
          <button
            type="button"
            data-grv-theme-btn
            aria-label="Перемкнути тему"
            className="grv-header-theme-btn w-8 h-8 flex items-center justify-center rounded border transition-all"
          >
            <Sun data-icon="sun" className="w-4 h-4" />
            <Moon data-icon="moon" className="w-4 h-4" />
          </button>
          {headerCta ? (
            <AppLink
              href={headerCta.href}
              data-grv-header-cta
              className="ml-2 px-5 py-2 bg-gold-gradient text-dark font-bold text-sm rounded hover:opacity-90 transition-opacity duration-300 shadow-lg"
            >
              {headerCta.label}
            </AppLink>
          ) : null}
        </nav>

        <div className="lg:hidden flex items-center gap-3">
          <button
            type="button"
            data-grv-theme-btn
            aria-label="Перемкнути тему"
            className="grv-header-theme-btn w-8 h-8 flex items-center justify-center rounded border transition-all"
          >
            <Sun data-icon="sun" className="w-4 h-4" />
            <Moon data-icon="moon" className="w-4 h-4" />
          </button>
          <button
            type="button"
            data-grv-menu-btn
            className="grv-header-menu-btn p-2 transition-colors"
            aria-expanded="false"
            aria-controls="grv-header-mobile"
            aria-label="Відкрити меню"
          >
            <Menu data-icon="menu" className="w-6 h-6" />
            <X data-icon="close" className="w-6 h-6" style={{ display: 'none' }} />
          </button>
        </div>
      </div>

      <div
        id="grv-header-mobile"
        className="grv-header-mobile lg:hidden backdrop-blur-md border-t"
        data-grv-header-mobile
        hidden
      >
        <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
          {navLinks.map((link) => {
            const isActive = isNavItemActive(pathname, link.href);
            return (
              <AppLink
                key={`${link.label}-${link.href}`}
                href={link.href}
                data-grv-nav
                data-grv-mobile-nav
                data-active={isActive ? 'true' : undefined}
                className="grv-header-nav block px-3 py-2.5 text-sm font-medium transition-colors"
              >
                {link.label}
              </AppLink>
            );
          })}
          {headerCta ? (
            <div className="pt-3 pb-1">
              <AppLink
                href={headerCta.href}
                data-grv-header-cta
                data-grv-mobile-nav
                className="block text-center px-5 py-3 bg-gold-gradient text-dark font-bold text-sm rounded"
              >
                {headerCta.label}
              </AppLink>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
